import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * TelemetryConsentModal — one-time informational notice.
 *
 * Behavioural-analytics collection (typing rhythm, cursor movement, focus)
 * is mandatory for every Melian user — it powers Obondo's coaching engine
 * and Kimani's baseline. Opting out is permanently disabled (2026-09-01
 * directive); this dialog only acknowledges the notice, it never asks the
 * user to choose. Renders once for authenticated users who have not yet
 * acknowledged. Sets `user.telemetry_consent = true` on the server and
 * patches the auth context optimistically.
 *
 * `active` / `onSettled`: see `App.js`'s `ConsentCoordinator` — this modal
 * only takes its turn after `ConsentRenewalModal` has settled, so the two
 * never race and flicker on top of each other.
 */
export default function TelemetryConsentModal({ active = true, onSettled }) {
    const { user, setUser } = useAuth() || {};
    const [open, setOpen]           = useState(false);
    const [busy, setBusy]           = useState(false);
    // Mount-once guard (see ConsentRenewalModal) — a transient `user`
    // object reference change must never re-open this modal once we've
    // already decided whether it needs to show.
    const checkedRef = useRef(false);

    useEffect(() => {
        if (!active || checkedRef.current) return;
        if (!user || typeof user !== "object") return;
        checkedRef.current = true;
        if (user.telemetry_consent === undefined || user.telemetry_consent === null) {
            const suppressed = localStorage.getItem("consent-suppressed-until") || "0";
            if (Date.now() > Number(suppressed)) {
                setOpen(true);
                return;
            }
        }
        onSettled?.();
    }, [active, user, onSettled]);

    async function acknowledge() {
        setBusy(true);
        try {
            await api.post("/prefs/telemetry-consent", { consent: true });
            // Optimistic patch instead of a full `refresh()` — avoids
            // re-running the `/auth/me` + locale-detect cascade in
            // AuthContext just to flip one boolean we already know the
            // value of.
            if (typeof setUser === "function") {
                setUser((prev) => (prev && typeof prev === "object" ? { ...prev, telemetry_consent: true } : prev));
            }
        } finally {
            localStorage.setItem("consent-suppressed-until", String(Date.now() + 24 * 3600_000));
            setBusy(false);
            setOpen(false);
            onSettled?.();
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md" data-testid="telemetry-consent-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-primary" /> Research & Behavioral Analytics
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p>
                        Melian Dialogue measures your typing rhythm, cursor movements, and
                        page focus (no keystroke content) to power Obondo's cognitive
                        interventions and Kimani's behavioural baseline, and to
                        contribute anonymised data to academic behavioural-finance
                        research.
                    </p>
                    <p className="text-muted-foreground">
                        Fully compliant with Kenya DPA and GDPR. Your user ID is
                        SHA-256 anonymised before persistence. This collection is
                        required to use Melian Dialogue and cannot be turned off.
                    </p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button onClick={acknowledge} disabled={busy}
                            data-testid="consent-accept">Got it</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
